import gettext from 'js-utils/gettext';
import { logCheckpointViaAjax } from 'js-utils/janus';

import {
    eventPublishStart,
    eventPublishFailure,
    eventUnpublishStart,
    eventUnpublishSuccess,
    eventUnpublishFailure,
    eventCancelStart,
    eventCancelSuccess,
    eventCancelFailure,
    eventDeleteStart,
    eventDeleteSuccess,
    eventDeleteFailure,
    cancelSchedulePublishFailure,
    saveSalesStatusStart,
    saveSalesStatusSuccess,
    saveSalesStatusFailure,
    endOnlineSalesFailure,
} from './actions';
import {
    getErrorStringFromTranslatedError,
    getRedirectURLForMarketingToolsExperiment,
    gtmTrackingPixelEvent,
    formatSalesStatusData,
} from './utils';
import {
    cancelEvent,
    cancelSchedulePublishErrorSpec,
    deleteEvent,
    publishEvent,
    savePublishSettings,
    schedulePublishErrorSpec,
    unpublishEvent,
    saveSalesStatus as saveSalesStatusAPI,
    endOnlineSales as endOnlineSalesAPI,
    endOnlineSalesErrorSpec,
} from './api';
import {
    FIRST_CREATE_EVENT_PAID_PUBLISH,
    FIRST_CREATE_EVENT_PUBLISH,
    CREATE_EVENT_PUBLISH,
    SALES_STATUS_CUSTOM_VALUE,
    GA_TRACKING_EVENT_CATEGORY,
    GA_GOAL_CATEGORY,
    LABEL_NEW_CREATE,
    ACTION_EVENT_PUBLISH,
    ACTION_FIRST_PUBLISH,
    ACTION_FIRST_PAID_PUBLISH,
} from '../constants';
import { setWindowLocation } from 'js-utils/http';
import { trackAnalyticsEvent } from '@eb/site-analytics';
import { formatForAPI } from 'js-utils/datetime';

export const GTM_PIXEL_FIRST_PUBLISH = 'First Publish';
export const GTM_PIXEL_INCREMENTAL_PUBLISH = 'Incremental Publish';
export const GTM_PIXEL_FIRST_PAID_PUBLISH = 'First Paid Publish';

/**
 * Wraps a call to `publishEvent` with action dispatches
 */
export const dispatchPublishEvent = ({
    dispatch,
    eventId,
    guidedChecklistVariant,
    hasPublish,
    hasPaidPublish,
    isPaidEvent,
    isMarketingToolsExperimentEnabled = false,
}) => {
    dispatch(eventPublishStart());

    return publishEvent(eventId)
        .catch((error) => {
            dispatch(
                eventPublishFailure(
                    getErrorStringFromTranslatedError(
                        error,
                        gettext(
                            'There has been a problem with your fetch operation: %(message)s',
                            { message: error.message },
                        ),
                    ),
                    error,
                ),
            );
            throw error;
        })
        .then(() => {
            const eventCheckpointPromises = [];

            trackAnalyticsEvent({
                action: ACTION_EVENT_PUBLISH,
                category: GA_TRACKING_EVENT_CATEGORY,
                label: LABEL_NEW_CREATE,
                onWindowUnload: true,
                dimensions: { eventId },
            });

            if (!hasPublish) {
                trackAnalyticsEvent({
                    action: ACTION_FIRST_PUBLISH,
                    category: GA_GOAL_CATEGORY,
                    label: LABEL_NEW_CREATE,
                    onWindowUnload: true,
                    eventId,
                });
                gtmTrackingPixelEvent(GTM_PIXEL_FIRST_PUBLISH);

                eventCheckpointPromises.push(
                    logCheckpointViaAjax(FIRST_CREATE_EVENT_PUBLISH, {
                        event_id: eventId,
                    }),
                );
            } else {
                gtmTrackingPixelEvent(GTM_PIXEL_INCREMENTAL_PUBLISH);
            }

            if (isPaidEvent && !hasPaidPublish) {
                trackAnalyticsEvent({
                    action: ACTION_FIRST_PAID_PUBLISH,
                    category: GA_GOAL_CATEGORY,
                    label: LABEL_NEW_CREATE,
                    onWindowUnload: true,
                    eventId,
                });
                gtmTrackingPixelEvent(GTM_PIXEL_FIRST_PAID_PUBLISH);
                eventCheckpointPromises.push(
                    logCheckpointViaAjax(FIRST_CREATE_EVENT_PAID_PUBLISH, {
                        event_id: eventId,
                    }),
                );
            }

            eventCheckpointPromises.push(
                logCheckpointViaAjax(CREATE_EVENT_PUBLISH, {
                    event_id: eventId,
                }),
            );

            const standardRedirectURL = `/myevent?eid=${eventId}&shouldshowpublishbanner=1`;
            const redirectURL = isMarketingToolsExperimentEnabled
                ? getRedirectURLForMarketingToolsExperiment(
                      eventId,
                      guidedChecklistVariant,
                  )
                : standardRedirectURL;

            Promise.all(eventCheckpointPromises).finally(() => {
                setWindowLocation(redirectURL);
            });
        });
};

/**
 * Wraps a call to `unpublishEvent` with action dispatches
 */
export const dispatchUnpublishEvent = (dispatch, eventId) => {
    dispatch(eventUnpublishStart());

    return unpublishEvent(eventId)
        .catch((error) => {
            dispatch(
                eventUnpublishFailure(
                    getErrorStringFromTranslatedError(
                        error,
                        gettext(
                            'There has been a problem with your fetch operation: %(message)s',
                            { message: error.message },
                        ),
                    ),
                    error,
                ),
            );
            throw error;
        })
        .then((result) => {
            dispatch(eventUnpublishSuccess(result));
        });
};

/**
 * Wraps a call to `cancelEvent` with action dispatches
 */
export const dispatchCancelEvent = (dispatch, eventId) => {
    dispatch(eventCancelStart());

    return cancelEvent(eventId)
        .catch((error) => {
            dispatch(
                eventCancelFailure(
                    getErrorStringFromTranslatedError(
                        error,
                        gettext(
                            'There has been a problem with your fetch operation: %(message)s',
                            { message: error.message },
                        ),
                    ),
                    error,
                ),
            );
            throw error;
        })
        .then((result) => {
            dispatch(eventCancelSuccess(result));
        });
};

/**
 * Wraps a call to `deleteEvent` with action dispatches
 */
export const dispatchDeleteEvent = (dispatch, eventId) => {
    dispatch(eventDeleteStart());

    return deleteEvent(eventId)
        .catch((error) => {
            dispatch(
                eventDeleteFailure(
                    getErrorStringFromTranslatedError(
                        error,
                        gettext(
                            'There has been a problem with your fetch operation: %(message)s',
                            { message: error.message },
                        ),
                    ),
                    error,
                ),
            );
            throw error;
        })
        .then((result) => {
            dispatch(eventDeleteSuccess(result));
            setWindowLocation('/myevents');
        });
};

export const dispatchCancelSchedulePublish = (dispatch, eventId) => {
    const data = {
        publish_settings: {
            schedule_publish_date: null,
        },
    };

    return savePublishSettings(
        data,
        cancelSchedulePublishErrorSpec,
        eventId,
    ).catch((error) => {
        dispatch(
            cancelSchedulePublishFailure(
                getErrorStringFromTranslatedError(error),
            ),
        );
        throw error;
    });
};

export const schedulePublishEvent = (
    eventId,
    startDate,
    startTime,
    timeZone,
) => {
    const publishDate = formatForAPI(startDate, startTime, timeZone);
    const schedulePublishBody = {
        publish_settings: {
            schedule_publish_date: publishDate,
        },
    };

    return savePublishSettings(
        schedulePublishBody,
        schedulePublishErrorSpec,
        eventId,
    );
};

const saveSalesStatus = (data, eventId, dispatch) => {
    const eventTextKey = Object.keys(data.texts)[0];

    return saveSalesStatusAPI(data, eventId, eventTextKey)
        .catch((error) => {
            dispatch(
                saveSalesStatusFailure(
                    getErrorStringFromTranslatedError(error),
                ),
            );
            throw error;
        })
        .then((result) => dispatch(saveSalesStatusSuccess(result)));
};

export const changeSalesStatus = (eventId, values) => (dispatch, getState) => {
    dispatch(saveSalesStatusStart);

    const {
        eventSalesStatus: { isNotYetOnSale, isSoldOut },
    } = getState();
    const { statusCode, customMessage } = values;
    const data = formatSalesStatusData({
        statusCode,
        customMessage,
        isNotYetOnSale,
        isSoldOut,
    });

    // If the event is not yet on sale and the user selects custom status
    // or the event is sold out, we don't end online ticket sales.
    if (
        (isNotYetOnSale && statusCode === SALES_STATUS_CUSTOM_VALUE) ||
        isSoldOut
    ) {
        return saveSalesStatus(data, eventId, dispatch);
    }

    // By default we end online ticket sales.
    return endOnlineSalesAPI(eventId).then(({ response }) => {
        if (response.ok) {
            return saveSalesStatus(data, eventId, dispatch);
        }
        dispatch(
            endOnlineSalesFailure(
                endOnlineSalesErrorSpec[response.statusText] ||
                    endOnlineSalesErrorSpec.DEFAULT,
            ),
        );

        return Promise.reject();
    });
};
